import { AlgoliaTrail, trailFromAlgoliaTrail } from '@alltrails/trails';
import useFeatures from '@alltrails/features/hooks/useFeatures';
import InfoDividerDot from '@alltrails/shared/components/cards/InfoDividerDot';
import { getUrlFromSlug } from '@alltrails/shared/utils/cardLinks';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import logSaveClicked from '@alltrails/amplitude/events/logSaveClicked';
import LinkInfoRenderer from '@alltrails/denali/components/LinkInfoRenderer';
import { metersToFormattedDistance, ratingToFormattedRating } from '@alltrails/shared/utils/dataConversions';
import useDifficultyText from '@alltrails/shared/hooks/useDifficultyText';
import { useLanguageRegionCode } from '@alltrails/language/hooks/useLanguageRegionCode';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { PrivacyPolicyOptions } from '@alltrails/shared/types/privacyPolicy';
import { forwardRef, useCallback } from 'react';
import Link from '@alltrails/denali/components/Link';
import TapFeature from '@alltrails/amplitude/enums/TapFeature';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import { getFormattedHoursMinutesWithUnitsInSpans } from '@alltrails/data-formatting';
import { getScreenReaderRatingText } from '@alltrails/core';
import { useLocationMap } from '../LocationMap/LocationMapContext';
import styles from './TopResults.module.scss';
import TrailCarousel from './TrailCarousel';
import BookmarkButtonWrapper from './BookmarkButtonWrapper';
import TrailCardShowMoreText from './TrailCardShowMoreText';
import { useSelector } from '@/utils/redux';

type Props = {
  trail: AlgoliaTrail;
  className?: string;
  ranking: number;
  isFavorite: boolean;
  onFavoriteClick: (itemType: 'trail' | 'map' | 'track', itemId: number, itemPrivacy?: PrivacyPolicyOptions | undefined) => void;
  isShowMoreClicked?: boolean;
  isTabletOrMobile?: boolean;
  focusable?: boolean;
};

const TopTrailsCard = forwardRef<HTMLDivElement, Props>(({
  trail,
  className,
  ranking,
  isFavorite,
  onFavoriteClick,
  isShowMoreClicked,
  isTabletOrMobile,
  focusable = false
}, ref) => {
  const trailToRender = trailFromAlgoliaTrail(trail);
  const {
    name,
    avgRating,
    defaultActivityStats: { difficulty } = {},
    trailCounts: { reviewCount },
    trailGeoStats: { length, durationMinutes },
    trailDetail
  } = trailToRender;
  const displayMetric = useSelector(state => state.context.displayMetric);
  const languageRegionCode = useLanguageRegionCode();
  const { setCardHoveredResult } = useLocationMap();
  const intl = useIntl();
  const publicBaseUrl = usePublicBaseUrl();
  const href = getUrlFromSlug(`/${trail.slug}`, publicBaseUrl, languageRegionCode);
  const localizedRating = ratingToFormattedRating(avgRating, languageRegionCode);
  const showStrenuousDifficultyLevel = useFeatures()?.includes('show_strenuous_difficulty_level');
  const difficultyText = useDifficultyText(difficulty, showStrenuousDifficultyLevel);
  const minutes = parseInt(durationMinutes ?? '0', 10);
  const isRoundEstTimeToCompletionEnabled = useFeatures()?.includes('round_est_time_completion');

  const handleTrailClick = useCallback(
    (tapFeature: TapFeature) => {
      logTrailCardClicked({
        trail_id: String(trail.ID),
        card_location: 'seo',
        detailed_card_location: 'highlights carousel',
        tap_feature: tapFeature
      });
    },
    [trail.ID]
  );

  const handleFavoriteClick = () => {
    logSaveClicked({
      detailed_card_location: 'location page',
      save_location: 'explore',
      map_id: trail.ID,
      map_type: 'trail'
    });
    onFavoriteClick('trail', trail.ID);
  };

  const handleSetHoveredResult = useCallback(() => {
    if (trail._geoloc !== undefined) {
      setCardHoveredResult({
        _geoloc: trail._geoloc,
        _cluster_geoloc: trail._cluster_geoloc,
        ID: String(trail.ID),
        type: trail.type
      });
    }
  }, [setCardHoveredResult, trail.ID, trail._cluster_geoloc, trail._geoloc, trail.type]);

  const handleClearHoveredResult = useCallback(() => setCardHoveredResult(undefined), [setCardHoveredResult]);
  const hasDescription = Boolean(trailDetail?.description);

  return (
    <div
      ref={ref}
      role="group"
      aria-label="trail"
      className={classNames(styles.trailCardSnippetContainer, className)}
      title={name}
      {...(focusable && { tabIndex: -1 })}
      onMouseEnter={handleSetHoveredResult}
      onMouseLeave={handleClearHoveredResult}
    >
      <div className={styles.resultCardContent}>
        <Link href={href} target="_blank" onClick={() => handleTrailClick('trail image')} outsideOfMugen>
          <TrailCarousel trail={trailToRender} defaultUrl={!isShowMoreClicked ? trail.profile_photo_url : undefined} />
        </Link>
        <div className={styles.titleAndBookmark}>
          <div className={styles.titleAndStats}>
            <div className={styles.rankNameAndBookmark}>
              <h3 className={styles.trailCardHeading}>
                <LinkInfoRenderer
                  linkInfo={{ href, outsideOfMugen: true, target: '_blank' }}
                  className={styles.resultName}
                  onClick={() => handleTrailClick('trail name')}
                >
                  <span className={styles.desktopRanking}>#{ranking} - </span>
                  {name}
                </LinkInfoRenderer>
              </h3>
              {!isTabletOrMobile && <BookmarkButtonWrapper isFavorite={isFavorite} onClick={handleFavoriteClick} />}
            </div>
            <Link className={styles.resultStatsLink} href={href} outsideOfMugen target="_blank" title={name}>
              <div className={styles.resultStats}>
                {!!avgRating && (
                  <span className={styles.star} role="img" aria-label={getScreenReaderRatingText(intl, avgRating)}>
                    <StarFilled color="currentColor" />
                  </span>
                )}
                <div className={styles.textContent}>
                  {!!avgRating && (
                    <span className={styles.rating} data-testid="rating">
                      {localizedRating}
                    </span>
                  )}
                  {!!reviewCount && (
                    <span className={styles.reviews} data-testid="review-count">
                      ({reviewCount})
                    </span>
                  )}
                  {!!difficulty && (
                    <div data-testid="difficulty" className={styles.resultStat}>
                      {(!!avgRating || !!reviewCount) && <InfoDividerDot className={styles.infoDot} />}
                      <span>{difficultyText}</span>
                    </div>
                  )}
                  {!!length && (
                    <div data-testid="distance" className={styles.resultStat}>
                      {(!!difficulty || !!reviewCount) && <InfoDividerDot className={styles.infoDot} />}
                      <span>{metersToFormattedDistance(intl, languageRegionCode, length, displayMetric ? 'km' : 'mi', 1)}</span>
                    </div>
                  )}
                  {!!minutes && (
                    <div className={styles.estimatedTime} data-testid="duration">
                      {isRoundEstTimeToCompletionEnabled && trail?.estimated_time_to_complete ? (
                        <>
                          <InfoDividerDot className={styles.infoDot} />
                          {`${intl.formatMessage({ defaultMessage: ' Est.' })} ${trail.estimated_time_to_complete.value} ${trail.estimated_time_to_complete.unit}`}
                        </>
                      ) : (
                        <>
                          <InfoDividerDot className={styles.infoDot} />
                          {intl.formatMessage({ defaultMessage: ' Est.' })} {getFormattedHoursMinutesWithUnitsInSpans(intl, minutes * 60, true, true)}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Link>
            {!isTabletOrMobile && hasDescription && (
              <TrailCardShowMoreText
                trailId={trail.ID}
                trailSlug={trail.slug}
                trailName={name}
                className={styles.trailCardSnippetDescription}
                testIdPrefix={`${trail.id}-description-main`}
                description={trailDetail?.description}
              />
            )}
          </div>
          {isTabletOrMobile && <BookmarkButtonWrapper isFavorite={isFavorite} onClick={handleFavoriteClick} />}
        </div>
      </div>
      {isTabletOrMobile && hasDescription && (
        <div className={styles.trailCardSnippetDescriptionContainer}>
          <TrailCardShowMoreText
            trailId={trail.ID}
            trailSlug={trail.slug}
            trailName={name}
            className={styles.trailCardSnippetDescription}
            testIdPrefix={`${trail.id}-description-snippet`}
            description={trailDetail?.description}
          />
        </div>
      )}
    </div>
  );
});

TopTrailsCard.displayName = 'TopTrailsCard';

export default TopTrailsCard;
